import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

import useFinanceCategorias from "@/hooks/useFinanceCategorias";
import HeroCategoriasCard from "./categorias/HeroCategoriasCard";
import DistribuicaoCategoriasDonut from "./categorias/DistribuicaoCategoriasDonut";
import CategoriaTable from "./categorias/CategoriaTable";
import CategoriaModal from "./categorias/CategoriaModal";
import CategoriasToolbar from "./categorias/CategoriasToolbar";

// FIN-5 — CategoriasTab orquestrador.
// Hierarquia: Hero → Toolbar (T8) → 2 colunas Donut+Tabela (T5/T6) →
// Modal (T7).
//
// T4 entrega Hero adaptativo + shell + hook. Próximas tasks preenchem.

const ROW_TRANSITION = { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] };

export default function CategoriasTab({ onTalk }) {
  const cat = useFinanceCategorias();
  const [modalState, setModalState] = useState({ open: false, category: null });

  const openCreate = () => setModalState({ open: true, category: null });
  const openEdit = (row) => {
    // row é a linha do breakdown (tem category_id + meta)
    const fullCat = cat.categories.find(c => c.id === row.category_id) || row;
    setModalState({ open: true, category: fullCat });
  };
  const closeModal = () => setModalState({ open: false, category: null });

  const handleDelete = async (row) => {
    if (!row.category_id) {
      toast.error("Categoria sem ID — não posso apagar");
      return;
    }
    try {
      await cat.deleteCategory(row.category_id);
      toast.success(`Categoria "${row.name}" removida`);
    } catch (err) {
      if (err.code === "in_use") {
        const n = err.transactionsCount;
        const msg = `"${row.name}" tem ${n} transação${n === 1 ? "" : "ões"}. Apagar mesmo assim? As transações continuam mas a categoria some das listas.`;
        if (window.confirm(msg)) {
          try {
            await cat.deleteCategory(row.category_id, { force: true });
            toast.success(`"${row.name}" removida`);
          } catch (e2) {
            toast.error(e2.message || "Falha ao apagar");
          }
        }
      } else {
        toast.error(err.message || "Falha ao apagar");
      }
    }
  };

  return (
    <div className="space-y-5 md:space-y-6" data-testid="finance-categorias">
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={ROW_TRANSITION}
      >
        <HeroCategoriasCard
          breakdown={cat.breakdown}
          month={cat.month}
          onChangeMonth={cat.setMonth}
          loading={cat.isLoading}
        />
      </motion.section>

      {/* Toolbar (filtro conta + botão criar) */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.06 }}
      >
        <CategoriasToolbar
          accounts={cat.accounts}
          accountIds={cat.accountIds}
          onChangeAccountIds={cat.setAccountIds}
          onCreate={openCreate}
        />
      </motion.section>

      {/* Donut + lista (esquerda) */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.10 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6"
        data-testid="cat-2cols"
      >
        <DistribuicaoCategoriasDonut breakdown={cat.breakdown} isLoading={cat.isLoading} />
        <CategoriaTable
          rows={cat.breakdown?.breakdown || []}
          isLoading={cat.isLoading}
          onDrillDown={(row) => {
            // Drill down: navega pra Extrato com categoria pré-aplicada.
            // Usamos location.assign pra garantir que hashchange dispare
            // (history.pushState + reescrever hash não dispara o evento
            // se o hash for igual ao corrente, e nem sempre a tab já está
            // ativa).
            const params = new URLSearchParams();
            if (row.name) params.set("category", row.name);
            const newSearch = params.toString();
            const url = window.location.pathname + (newSearch ? `?${newSearch}` : "") + "#financeiro/extrato";
            window.location.assign(url);
          }}
          onCreate={openCreate}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      </motion.section>
      {/* T7 — Modal CRUD com emoji/color picker */}
      {/* T8 — Filtros conta + mês integration */}

      {cat.error && (
        <div
          className="text-caption text-status-error px-2"
          data-testid="cat-error"
        >
          {cat.error}
        </div>
      )}

      <CategoriaModal
        open={modalState.open}
        onOpenChange={(o) => { if (!o) closeModal(); }}
        category={modalState.category}
        onCreate={cat.createCategory}
        onUpdate={cat.updateCategory}
        knownNames={cat.knownCategoryNames}
      />
    </div>
  );
}
